import React, { useState, useCallback } from 'react';
import { uploadImage } from '../../services/upload.service';
import ImageLinkInput from './ImageLinkInput';

const MAX_IMAGES = 7;

/**
 * Multiple-image version of ImageDropZone — drag several files at once,
 * paste more than one screenshot, or add one at a time up to MAX_IMAGES.
 * Jenn, 2026-08-27: "we use these to show stages in what's happening" —
 * a single-image field couldn't cover a multi-step screenshot sequence.
 */
export default function MultiImageDropZone({ urls, onChange }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const remaining = MAX_IMAGES - urls.length;

  const handleFiles = useCallback(async (fileList) => {
    const files = [...fileList].filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    if (files.length > remaining) {
      setError(`Only ${remaining} more image${remaining === 1 ? '' : 's'} allowed (max ${MAX_IMAGES}).`);
    } else {
      setError(null);
    }
    const toUpload = files.slice(0, remaining);
    if (toUpload.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        uploaded.push(await uploadImage(file));
      }
      onChange([...urls, ...uploaded]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload one or more images.');
    } finally {
      setUploading(false);
    }
  }, [urls, remaining, onChange]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files || []);
  };

  const handlePaste = (e) => {
    const items = [...(e.clipboardData?.items || [])].filter(i => i.type.startsWith('image/'));
    if (items.length) {
      e.preventDefault();
      handleFiles(items.map(i => i.getAsFile()));
    }
  };

  const removeAt = (i) => onChange(urls.filter((_, idx) => idx !== i));

  return (
    <div>
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {urls.map((url, i) => (
            <div key={url} className="relative group">
              <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-gray-200 text-gray-500 text-xs leading-none flex items-center justify-center hover:text-red-600 hover:border-red-200 shadow-sm"
                title="Remove image"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {remaining > 0 && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onPaste={handlePaste}
          onClick={() => document.getElementById('multi-image-input')?.click()}
          tabIndex={0}
          className={`border-2 border-dashed rounded-lg px-3 py-4 text-center text-xs cursor-pointer transition-colors
            ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <input
            id="multi-image-input"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files || [])}
          />
          {uploading ? (
            <span className="text-gray-400">Uploading…</span>
          ) : (
            <span className="text-gray-400">
              Drag images here, paste one or more, or click to browse ({urls.length}/{MAX_IMAGES})
            </span>
          )}
        </div>
      )}

      {remaining > 0 && <ImageLinkInput onLink={(url) => onChange([...urls, url])} disabled={uploading} buttonLabel="Add link" />}
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
}
